import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { type AffiliateCommissionRow, formatCommissionAmount, maskReferredUserId } from "@/lib/affiliateDashboard";
import type { PayableRow, TaxStatus } from "./types";

type Props = {
  dialogRow: PayableRow | null;
  setDialogRow: (r: PayableRow | null) => void;
  dialogCommissions: AffiliateCommissionRow[];
  dialogLoading: boolean;
  paymentReference: string;
  setPaymentReference: (v: string) => void;
  notes: string;
  setNotes: (v: string) => void;
  allowMissingTax: boolean;
  setAllowMissingTax: (v: boolean) => void;
  taxStatus: TaxStatus;
  submitting: boolean;
  exportBatchCsv: () => void;
  submitBatch: () => void;
};

const TaxBlock = ({ taxStatus, allowMissingTax, setAllowMissingTax }: Pick<Props, "taxStatus" | "allowMissingTax" | "setAllowMissingTax">) => {
  const ts = taxStatus?.status ?? "missing";
  if (ts === "verified") {
    return (
      <div className="rounded-md border border-border bg-muted/30 p-2 text-xs text-muted-foreground">
        ✓ Tax form verified{taxStatus?.expires_at ? ` · expires ${new Date(taxStatus.expires_at).toLocaleDateString()}` : ""}.
      </div>
    );
  }
  const label =
    ts === "submitted" ? "Tax form submitted but not yet verified."
      : ts === "rejected" ? "Tax form was rejected — affiliate must resubmit."
      : ts === "expired" ? "Tax form on file has expired."
      : "No tax form on file for this affiliate.";
  return (
    <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs">
      <div className="text-destructive">{label} Payout will be blocked.</div>
      <label className="flex items-start gap-2 text-foreground">
        <Checkbox checked={allowMissingTax} onCheckedChange={(v) => setAllowMissingTax(v === true)} />
        <span>Override and pay anyway (acknowledges compliance risk; logged to audit).</span>
      </label>
    </div>
  );
};

export const MarkPaidDialog = (p: Props) => (
  <Dialog open={!!p.dialogRow} onOpenChange={(o) => !o && p.setDialogRow(null)}>
    <DialogContent className="max-w-xl">
      <DialogHeader>
        <DialogTitle>Mark payout batch as paid</DialogTitle>
        <DialogDescription>
          {p.dialogRow ? (
            <>
              {formatCommissionAmount(p.dialogRow.payable_cents, p.dialogRow.currency)} across{" "}
              {p.dialogRow.commission_count} commission(s) for{" "}
              <span className="font-mono">{maskReferredUserId(p.dialogRow.affiliate_user_id)}</span>
            </>
          ) : null}
        </DialogDescription>
      </DialogHeader>
      {p.dialogLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : (
        <>
          {p.dialogRow && !p.dialogRow.meets_minimum ? (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
              This affiliate is below the configured minimum payout threshold. Proceed only if
              you have approval to override.
            </p>
          ) : null}
          <TaxBlock taxStatus={p.taxStatus} allowMissingTax={p.allowMissingTax} setAllowMissingTax={p.setAllowMissingTax} />
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="ref">Payment reference</Label>
              <Input
                id="ref"
                placeholder="Stripe transfer id, wire reference, PayPal txn…"
                value={p.paymentReference}
                onChange={(e) => p.setPaymentReference(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea id="notes" rows={2} value={p.notes} onChange={(e) => p.setNotes(e.target.value)} />
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {p.dialogCommissions.length} commission(s) will flip to <strong>paid</strong>.
              </p>
              <Button type="button" variant="ghost" size="sm" onClick={p.exportBatchCsv} disabled={p.dialogCommissions.length === 0}>
                <Download className="mr-1 h-3 w-3" />
                Batch CSV
              </Button>
            </div>
          </div>
        </>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={() => p.setDialogRow(null)}>Cancel</Button>
        <Button onClick={p.submitBatch} disabled={p.submitting || p.dialogLoading || p.dialogCommissions.length === 0}>
          {p.submitting ? "Marking paid…" : "Mark batch paid"}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
